import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, Phone } from 'lucide-react';
import Button from '../../common/Button/Button';
import { validateForm, validateField, VALIDATION_RULES, ErrorMessage } from '../../../utils/formValidation';
import { toast } from 'react-hot-toast';

const LoginForm = ({ onLogin, onForgotPassword, onOTPLogin, loading = false }) => {
  const rememberMeValue = localStorage.getItem('remember_me');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    emailOrPhone: '',
    rememberMe: rememberMeValue === 'true' // Pre-populate from localStorage
  });

  // Track if this is initial load or user interaction
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update localStorage when rememberMe state changes (only on user interaction)
  useEffect(() => {
    // Skip localStorage update on initial load
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    // Only update localStorage on user interaction
    if (formData.rememberMe) {
      localStorage.setItem('remember_me', 'true');
    } else {
      localStorage.removeItem('remember_me');
    }
  }, [formData.rememberMe, isInitialLoad]);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [otpDeliveryMethod, setOtpDeliveryMethod] = useState('email'); // 'email' or 'phone'
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const getValidationRules = () => {
    if (loginMethod === 'password') {
      return {
        email: { ...VALIDATION_RULES.email, maxLength: 100 },
        password: VALIDATION_RULES.required
      };
    } else {
      if (otpDeliveryMethod === 'email') {
        return {
          emailOrPhone: {
            ...VALIDATION_RULES.email,
            maxLength: 100,
            customLabel: 'Email'
          }
        };
      } else {
        return {
          emailOrPhone: {
            ...VALIDATION_RULES.phone,
            customLabel: 'Phone number'
          }
        };
      }
    }
  };

  const validateFieldLevel = (name, value) => {
    const rules = getValidationRules();
    if (rules[name]) {
      const fieldErrors = validateField(name, value, rules[name]);
      setErrors(prev => ({
        ...prev,
        [name]: fieldErrors.length > 0 ? fieldErrors[0] : ''
      }));
      return fieldErrors.length === 0;
    }
    return true;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateFieldLevel(name, value);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'email') {
      // For email field in password login, limit to 100 characters
      const emailValue = value.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else if (name === 'emailOrPhone') {
      if (otpDeliveryMethod === 'phone') {
        // For phone number input, limit to 10 digits and only allow numbers
        const phoneDigits = value.replace(/\D/g, '').slice(0, 10);
        setFormData(prev => ({
          ...prev,
          [name]: phoneDigits
        }));
      } else if (otpDeliveryMethod === 'email') {
        // For email in OTP login, limit to 100 characters
        const emailValue = value.slice(0, 100);
        setFormData(prev => ({
          ...prev,
          [name]: emailValue
        }));
      }
    } else {
      const newValue = type === 'checkbox' ? checked : value;

      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: newValue
        };
        return newFormData;
      });
    }

    // For real-time validation (optional, can be noisy)
    // We'll clear errors as they type, but validate format for things like phone numbers
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleOtpDeliveryChange = (method) => {
    setOtpDeliveryMethod(method);
    // Reset the input field when changing delivery method
    setFormData(prev => ({
      ...prev,
      emailOrPhone: '' // Clear field when switching methods
    }));
    // Clear any existing errors
    setErrors(prev => ({
      ...prev,
      emailOrPhone: ''
    }));
  };

  const handleKeyDown = (e) => {
    const { name, value } = e.target;

    // Handle Enter key for form submission
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
      return;
    }

    // Prevent input if maximum length reached
    if (name === 'email' && value.length >= 100) {
      // Allow backspace, delete, arrow keys, tab, enter
      if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
    } else if (name === 'password' && value.length >= 72) {
      // Allow backspace, delete, arrow keys, tab, enter
      if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
    } else if (name === 'emailOrPhone') {
      if (otpDeliveryMethod === 'phone') {
        // For phone input, prevent non-numeric characters
        if (value.replace(/\D/g, '').length >= 10) {
          // Allow backspace, delete, arrow keys, tab, enter
          if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
            e.preventDefault();
          }
        } else {
          // Prevent non-numeric characters for phone input
          // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
          if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true)) {
            return;
          }
          // Ensure that it is a number and stop the keypress
          if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
          }
        }
      } else if (otpDeliveryMethod === 'email' && value.length >= 100) {
        // Allow backspace, delete, arrow keys, tab, enter
        if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
          e.preventDefault();
        }
      }
    }
  };

  const handlePaste = (e) => {
    const { name } = e.target;

    if (name === 'email') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const emailValue = pastedData.slice(0, 100);
      setFormData(prev => ({
        ...prev,
        [name]: emailValue
      }));
    } else if (name === 'password') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const passwordValue = pastedData.slice(0, 72);
      setFormData(prev => ({
        ...prev,
        [name]: passwordValue
      }));
    } else if (name === 'emailOrPhone') {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');

      if (otpDeliveryMethod === 'phone') {
        // For phone, extract only digits and limit to 10
        const phoneDigits = pastedData.replace(/\D/g, '').slice(0, 10);
        setFormData(prev => ({
          ...prev,
          [name]: phoneDigits
        }));
      } else if (otpDeliveryMethod === 'email') {
        // For email, limit to 100 characters
        const emailValue = pastedData.slice(0, 100);
        setFormData(prev => ({
          ...prev,
          [name]: emailValue
        }));
      }
    }
  };

  const validateFormData = () => {
    const rules = getValidationRules();
    const { errors: newErrors, isValid } = validateForm(formData, rules);

    // Customize error messages based on delivery method for OTP login
    if (loginMethod === 'otp' && newErrors.emailOrPhone) {
      const msg = newErrors.emailOrPhone;
      if (msg && typeof msg === 'string' && msg.includes('Email or Phone')) {
        newErrors.emailOrPhone = otpDeliveryMethod === 'email'
          ? msg.replace('Email or Phone', 'Email')
          : msg.replace('Email or Phone', 'Phone number');
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateFormData()) {
      if (loginMethod === 'password') {
        // Trim email before sending to prevent validation errors
        onLogin(formData.email.trim(), formData.password, formData.rememberMe);
      } else {
        // Trim emailOrPhone before sending to prevent validation errors
        onOTPLogin(formData.emailOrPhone.trim(), otpDeliveryMethod, formData.rememberMe);
      }
    } else {
      toast.error('Please fix the errors in the form before submitting.');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-agrosaf-green to-agrosaf-blue relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>

        <div className="flex flex-col justify-center px-12 text-white relative z-10">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <img src="/logo.png" alt="Company Logo" className="h-16 w-auto object-contain mr-4" />
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight">{process.env.REACT_APP_COMPANY_NAME}</span>
                {/* <span className="text-xs opacity-70 tracking-[0.2em] font-medium">PRIVATE LIMITED</span> */}
              </div>
            </div>
            {/* <p className="text-sm opacity-80">Business Management Platform</p> */}
          </div>

          {/* Main Title */}
          <div className="mb-8">
            <h1 className="text-6xl font-bold mb-4 max-w-md text-center">{(process.env.REACT_APP_COMPANY_NAME).split(' ')[0].toUpperCase()}</h1>
            <p className="text-xl opacity-90 max-w-md text-center">{process.env.REACT_APP_COMPANY_TAGLINE}</p>
          </div>

          {/* Info Box */}
          {/* <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20">
            <p className="text-sm mb-2">Secure your account with an additional layer of protection.</p>
            <p className="text-sm">Choose your preferred verification method.</p>
          </div> */}
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8 text-center">
            <div className="w-32 h-32 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
            </div>
          </div>

          {/* Form */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Login</h2>
            <p className="text-gray-600">Choose login method</p>
          </div>

          {/* Login Method Selection */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => setLoginMethod('password')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLoginMethod('password');
                  }
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${loginMethod === 'password'
                  ? 'bg-white text-agrosaf-green shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('otp')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLoginMethod('otp');
                  }
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${loginMethod === 'otp'
                  ? 'bg-white text-agrosaf-green shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                OTP
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off" data-form-type="other">
            {/* Password Login Fields */}
            {loginMethod === 'password' && (
              <>
                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      maxLength={100}
                      autoComplete="off"
                      data-form-type="other"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      className={`input-field pl-10 ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="admin@example.com"
                      autocomplete="email"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                  {/* Character counter for email */}
                  <div className="mt-1 text-xs text-gray-400 text-right">
                    {formData.email.length}/100 characters
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      maxLength={72}
                      autoComplete="new-password"
                      data-form-type="other"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowPassword(!showPassword);
                        }
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                  {/* Character counter for password */}
                  <div className="mt-1 text-xs text-gray-400 text-right">
                    {formData.password.length}/72 characters
                  </div>
                </div>
              </>
            )}

            {/* OTP Login Fields */}
            {loginMethod === 'otp' && (
              <>
                {/* Email Input Field - Only show when email is selected */}
                {otpDeliveryMethod === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="emailOrPhone"
                        value={formData.emailOrPhone}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        maxLength={100}
                        autoComplete="off"
                        data-form-type="other"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        className={`input-field pl-10 ${errors.emailOrPhone ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="admin@example.com"
                        autocomplete="email"
                      />
                    </div>
                    {errors.emailOrPhone && (
                      <p className="mt-1 text-sm text-red-600">{errors.emailOrPhone}</p>
                    )}
                    <div className="mt-1 text-xs text-gray-400 text-right">
                      {formData.emailOrPhone.length}/100 characters
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Remember Me & Password Actions - Only for Password Login */}
            {loginMethod === 'password' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Remember</span>
                </label>
                <button
                  type="button"
                  onClick={() => onForgotPassword(formData.email)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onForgotPassword(formData.email);
                    }
                  }}
                  className="text-sm text-agrosaf-green hover:text-agrosaf-darkGreen font-medium"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              {loginMethod === 'password' ? 'Login' : 'Send OTP'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
